<template>
  <div class="page-root">
    <main id="ai-effects" class="main-content px-4 py-1">
      <!-- 生成面板：支持 SSR -->

      <GenerativePanel
        :key="effectCode"
        :effectCode="effectCode"
        :effectType="effectType"
        :effectConfig="effectConfig"
      />

      <!-- 导言部分：已确定为 HTML 字符串，直接渲染（支持 SSR） -->
      <div
        v-if="hasHtmlPageStaticConfig"
        ref="htmlContentRef"
        class="text-white py-6"
        v-html="htmlPageStaticConfig"
      />
    </main>
  </div>
</template>

<script setup>
import {
  provide,
  computed,
  ref,
  watchEffect,
  onMounted,
  onUnmounted,
  nextTick,
} from "vue";
import GenerativePanel from "@/components/ai-effects/GenerativePanel.vue";

const props = defineProps({
  effectCode: {
    type: String,
    required: true,
  },
  effectType: {
    type: String,
    required: true,
  },
  effectConfig: {
    type: Object,
    default: null,
  },
});

const r2CachedFetch = useR2CachedFetch();

// key 必须包含 effectType：同一 code 在 video / image 下对应不同 HTML 路径，否则缓存与 watch 会错用旧数据
const effectHtmlKey = computed(
  () => `effect-html-${props.effectType}-${props.effectCode}`
);
const { data: effectHtmlResult } = await useAsyncData(
  effectHtmlKey,
  async () => {
    const key = effectHtmlKey.value;
    if (!props.effectCode) return { key, html: "" };
    const data = await r2CachedFetch(
      `/api/r2/ai/effects/${props.effectType}/html/${props.effectCode}.html`
    );
    return { key, html: typeof data === "string" ? data.trim() : "" };
  },
  {
    watch: [() => props.effectCode, () => props.effectType],
    default: () => ({ key: "", html: "" }),
    lazy: import.meta.client,
  }
);

// HTML 字符串形式的 pageStaticConfig（用于 v-html）
const htmlPageStaticConfig = computed(() => {
  if (effectHtmlResult.value?.key !== effectHtmlKey.value) return "";
  return effectHtmlResult.value?.html || "";
});

// HTML 字符串非空才渲染
const hasHtmlPageStaticConfig = computed(() => {
  return Boolean(htmlPageStaticConfig.value);
});

// ===== 公共部分 =====
// scrollToTop 支持参数: { smooth = true }，默认平滑滚动，传 { smooth: false } 可直接跳转
const scrollToTop = ({ smooth = true } = {}) => {
  if (typeof window === "undefined") return;
  const behavior = smooth ? "smooth" : "auto";
  console.log("scrollToTop called, smooth:", behavior);
  window.scrollTo({
    top: 0,
    behavior: behavior,
  });
};

// 通过 provide 将 scrollToTop 函数提供给子组件
provide("scrollToTop", scrollToTop);

// HTML 内容容器的 ref
const htmlContentRef = ref(null);

// 处理 HTML 内容中的点击事件（事件委托）
const handleHtmlContentClick = (event) => {
  const target = event.target;
  if (!target) return;

  // 检查是否有 data-scroll-to-top 属性
  if (target.hasAttribute("data-scroll-to-top")) {
    event.preventDefault();
    const smooth = target.getAttribute("data-scroll-to-top") !== "false";
    scrollToTop({ smooth });
    return;
  }

  // 检查是否有点击 scrollToTop 的标记（通过 class 或 data 属性）
  // 支持 class="scroll-to-top" 或 data-action="scrollToTop"
  if (
    target.classList.contains("scroll-to-top") ||
    target.getAttribute("data-action") === "scrollToTop" ||
    target.closest(".scroll-to-top") ||
    target.closest("[data-action='scrollToTop']")
  ) {
    event.preventDefault();
    scrollToTop();
    return;
  }

  // 检查 onclick 属性中是否包含 scrollToTop（兼容旧代码）
  const onclickAttr = target.getAttribute("onclick");
  if (onclickAttr && onclickAttr.includes("scrollToTop")) {
    event.preventDefault();
    scrollToTop();
    return;
  }
};

// v-html 区块在异步有内容后才渲染（或路由切换后类型变化），需在 ref 就绪时绑定 / 解绑
watchEffect((onCleanup) => {
  const el = htmlContentRef.value;
  if (!el) return;
  el.addEventListener("click", handleHtmlContentClick);
  onCleanup(() => {
    el.removeEventListener("click", handleHtmlContentClick);
  });
});

onMounted(() => {
  nextTick(() => {
    if (typeof window !== "undefined") {
      window.scrollToTop = scrollToTop;
    }
  });
});

onUnmounted(() => {
  if (typeof window !== "undefined" && window.scrollToTop) {
    delete window.scrollToTop;
  }
});

useHead(() => ({
  title: props.effectConfig?.page_config?.seoTitle,
  link: [
    {
      rel: "canonical",
      href: props.effectConfig?.page_config?.seoUrl,
    },
  ],
  meta: [
    {
      hid: "description",
      name: "description",
      content: props.effectConfig?.page_config?.seoDescription,
    },
    {
      hid: "og:site_name",
      property: "og:site_name",
      content: "SharkFoto",
    },
    {
      hid: "og:type",
      property: "og:type",
      content: "website",
    },
    {
      hid: "og:locale",
      property: "og:locale",
      content: "en_US",
    },
    {
      hid: "og:image",
      property: "og:image",
      content: props.effectConfig?.cover_url,
    },
    {
      hid: "og:url",
      property: "og:url",
      content: props.effectConfig?.page_config?.seoUrl,
    },
    {
      hid: "og:title",
      property: "og:title",
      content: props.effectConfig?.page_config?.seoTitle,
    },
    {
      hid: "og:description",
      property: "og:description",
      content: props.effectConfig?.page_config?.seoDescription,
    },
    {
      hid: "twitter:card",
      name: "twitter:card",
      content: "summary_large_image",
    },
    {
      hid: "twitter:url",
      name: "twitter:url",
      content: props.effectConfig?.page_config?.seoUrl,
    },
    {
      hid: "twitter:title",
      name: "twitter:title",
      content: props.effectConfig?.page_config?.seoTitle,
    },
    {
      hid: "twitter:image",
      name: "twitter:image",
      content: props.effectConfig?.cover_url,
    },
    {
      hid: "twitter:site",
      name: "twitter:site",
      content: "@Shark_Foto",
    },
    {
      hid: "twitter:description",
      name: "twitter:description",
      content: props.effectConfig?.page_config?.seoDescription,
    },
  ],
}));
</script>

<style scoped>
/* 可放置全页的基础样式 */
.page-root {
  min-height: 89vh;
}
.main-content {
  margin: 0 auto;
}
</style>
